import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import { Popover } from '@mui/material';
import { ErrorMessage } from 'formik';
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ReactNode,
} from 'react';
import {
  callingCodeForCountry,
  DEFAULT_PHONE_COUNTRY,
  filterPhoneCountries,
  flagImageUrl,
  formatNationalInput,
  getPhoneCountry,
  parseE164,
  PHONE_COUNTRY_SELECT_LIST,
  toE164,
} from '../../../util/phone';
import * as S from './styles';

type HandleChange = (e: ChangeEvent<HTMLInputElement>) => void;

export interface IPhoneInputProps {
  numberName: string;
  numberValue?: string | number | null;
  onChange: HandleChange;
  countryName?: string;
  countryValue?: string | null;
  label?: ReactNode | string;
  hideLabel?: boolean;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  widthSize?:
    | 'xxsmall'
    | 'xsmall'
    | 'small'
    | 'medium'
    | 'large'
    | 'xlarge'
    | 'xxlarge'
    | 'fullWidth';
  heightSize?: 'small' | 'medium' | 'large';
  className?: string;
}

const SIZE_WIDTH = {
  xxsmall: '6rem',
  xsmall: '10rem',
  small: '15rem',
  medium: '18rem',
  large: '20rem',
  xlarge: '25rem',
  xxlarge: '27rem',
  fullWidth: '100%',
};

const SIZE_HEIGHT = {
  small: '3rem',
  medium: '4rem',
  large: '5.5rem',
};

function CountryFlag({ iso }: { iso: string }) {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [iso]);

  if (failed) {
    return <S.FlagFallback>{iso}</S.FlagFallback>;
  }

  return (
    <S.FlagImg
      alt=""
      src={flagImageUrl(iso)}
      srcSet={`https://flagcdn.com/w80/${iso.toLowerCase()}.png 2x`}
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}

function emitChange(onChange: HandleChange, name: string, value: string) {
  onChange({
    target: { name, value },
  } as ChangeEvent<HTMLInputElement>);
}

export default function PhoneInput({
  numberName,
  numberValue,
  onChange,
  countryName,
  countryValue,
  label,
  hideLabel,
  placeholder,
  disabled,
  error,
  widthSize = 'fullWidth',
  heightSize = 'medium',
  className,
}: IPhoneInputProps) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownWidth, setDropdownWidth] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const fieldShellRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const incoming = numberValue == null ? '' : String(numberValue);
  const parsedIncoming = parseE164(incoming);
  const isControlledPair = Boolean(countryName);

  const [uncontrolledCountry, setUncontrolledCountry] = useState(
    parsedIncoming.country,
  );
  const [uncontrolledNational, setUncontrolledNational] = useState(
    parsedIncoming.national,
  );

  useEffect(() => {
    if (isControlledPair) return;

    const parsed = parseE164(incoming, uncontrolledCountry);
    setUncontrolledCountry(parsed.country);
    setUncontrolledNational(parsed.national);
  }, [incoming, isControlledPair]);

  const country = isControlledPair
    ? String(countryValue || DEFAULT_PHONE_COUNTRY)
    : uncontrolledCountry;
  const national = isControlledPair ? incoming : uncontrolledNational;
  const selected = getPhoneCountry(country);
  const displayCountry = selected?.iso ?? DEFAULT_PHONE_COUNTRY;
  const displayNumber = formatNationalInput(displayCountry, national);

  useEffect(() => {
    if (!open) {
      setSearchTerm('');
      return;
    }

    const anchorWidth = fieldShellRef.current?.getBoundingClientRect().width ?? 0;
    setDropdownWidth(Math.max(anchorWidth, 288));

    const focusSearch = window.requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(focusSearch);
  }, [open]);

  const filteredCountries = useMemo(
    () => filterPhoneCountries(PHONE_COUNTRY_SELECT_LIST, searchTerm),
    [searchTerm],
  );

  const applyCountryAndNational = (nextCountry: string, nextNational: string) => {
    if (countryName) {
      if (nextCountry !== country) {
        emitChange(onChange, countryName, nextCountry);
      }
      if (nextNational !== national) {
        emitChange(onChange, numberName, nextNational);
      }
      return;
    }

    setUncontrolledCountry(nextCountry);
    setUncontrolledNational(nextNational);
    emitChange(onChange, numberName, toE164(nextCountry, nextNational));
  };

  const handleCountrySelect = (iso: string) => {
    if (disabled) return;
    applyCountryAndNational(iso, national);
    setOpen(false);
  };

  const handleNumberChange = (raw: string) => {
    const trimmed = raw.trim();
    if (trimmed.startsWith('+')) {
      const parsedInternational = parseE164(trimmed, displayCountry);
      applyCountryAndNational(
        parsedInternational.country,
        parsedInternational.national,
      );
      return;
    }

    applyCountryAndNational(displayCountry, trimmed.replace(/\D/g, ''));
  };

  return (
    <S.Container
      $width={SIZE_WIDTH[widthSize]}
      ref={containerRef}
      className={className}
    >
      {!hideLabel && label ? (
        <label htmlFor={numberName}>{label}</label>
      ) : null}
      <S.FieldShell
        ref={fieldShellRef}
        $height={SIZE_HEIGHT[heightSize]}
        $disabled={disabled}
        $open={open}
      >
        <S.CountryButton
          type="button"
          disabled={disabled}
          onClick={() => {
            if (disabled) return;
            const width = fieldShellRef.current?.getBoundingClientRect().width ?? 0;
            setDropdownWidth(Math.max(width, 288));
            setOpen((previous) => !previous);
          }}
          title={selected?.title}
        >
          <CountryFlag iso={displayCountry} />
          <S.CallingCode>
            {callingCodeForCountry(displayCountry)}
          </S.CallingCode>
          <KeyboardArrowDown fontSize="small" />
        </S.CountryButton>
        <S.NumberInput
          id={numberName}
          name={numberName}
          type="tel"
          autoComplete="tel-national"
          disabled={disabled}
          placeholder={placeholder}
          value={displayNumber}
          onChange={(event) => handleNumberChange(event.target.value)}
        />
      </S.FieldShell>
      <Popover
        open={open}
        anchorEl={fieldShellRef.current}
        onClose={(event) => {
          if (
            event &&
            'target' in event &&
            fieldShellRef.current?.contains(event.target as Node)
          ) {
            return;
          }
          setOpen(false);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        marginThreshold={8}
        disableScrollLock
        PaperProps={{
          elevation: 0,
          sx: {
            minWidth: dropdownWidth || undefined,
            marginTop: '0.25rem',
            overflow: 'visible',
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
        }}
      >
        <S.Dropdown
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => {
            if (event.key === 'Enter') event.stopPropagation();
          }}
        >
          <S.SearchInput
            ref={searchInputRef}
            type="text"
            value={searchTerm}
            placeholder="Buscar país ou DDI"
            onChange={(event) => setSearchTerm(event.target.value)}
            onClick={(event) => event.stopPropagation()}
          />
          {filteredCountries.length === 0 ? (
            <S.Empty>Nenhum país encontrado</S.Empty>
          ) : (
            <S.CountryList>
              {filteredCountries.map((item) => (
                <S.CountryOption
                  key={item.iso}
                  $selected={item.iso === displayCountry}
                  onMouseDown={() => handleCountrySelect(item.iso)}
                >
                  <CountryFlag iso={item.iso} />
                  <S.CountryName>{item.name}</S.CountryName>
                  <S.CallingCode>+{item.callingCode}</S.CallingCode>
                </S.CountryOption>
              ))}
            </S.CountryList>
          )}
        </S.Dropdown>
      </Popover>
      {error ? (
        <ErrorMessage name={numberName}>
          {(msg) => <S.StyledError>{msg}</S.StyledError>}
        </ErrorMessage>
      ) : null}
    </S.Container>
  );
}
